import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '@/api/endpoints';
import { Alert, Empty, ErrorState, Loading } from '@/components/common/State';
import type { Category, CategorySavePayload } from '@/api/types';

interface FormState {
  categoryId: number | null;
  parentId: string;
  name: string;
  slug: string;
  sortOrder: string;
  active: boolean;
}

const EMPTY_FORM: FormState = {
  categoryId: null,
  parentId: '',
  name: '',
  slug: '',
  sortOrder: '0',
  active: true,
};

interface FlatCategory extends Category {
  childCount: number;
}

/** 트리를 화면 표시 순서대로 펼친다. depth 는 서버가 준 값을 그대로 쓴다. */
function flatten(nodes: Category[]): FlatCategory[] {
  return nodes.flatMap((node) => {
    const children = node.children ?? [];
    return [{ ...node, childCount: children.length }, ...flatten(children)];
  });
}

export default function AdminCategoryPage() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError, error: loadError, refetch } = useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: adminApi.categories,
  });

  const flat = useMemo(() => flatten(data ?? []), [data]);
  const isEdit = form.categoryId !== null;
  const editing = isEdit ? flat.find((c) => c.categoryId === form.categoryId) : undefined;
  // 하위를 가진 카테고리는 다른 카테고리 밑으로 옮길 수 없다(3단계가 되므로).
  const parentLocked = (editing?.childCount ?? 0) > 0;
  const parentCandidates = flat.filter((c) => c.depth === 1 && c.categoryId !== form.categoryId);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['admin', 'categories'] });
    queryClient.invalidateQueries({ queryKey: ['categories'] });
  }

  function reset() {
    setForm(EMPTY_FORM);
    setError(null);
  }

  const save = useMutation({
    mutationFn: ({ categoryId, body }: { categoryId: number | null; body: CategorySavePayload }) =>
      categoryId === null ? adminApi.createCategory(body) : adminApi.updateCategory(categoryId, body),
    onSuccess: (_, { categoryId }) => {
      setFeedback(categoryId === null ? '카테고리를 등록했습니다.' : '카테고리를 수정했습니다.');
      reset();
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const remove = useMutation({
    mutationFn: (categoryId: number) => adminApi.deleteCategory(categoryId),
    onSuccess: (_, categoryId) => {
      setFeedback('카테고리를 삭제했습니다.');
      if (form.categoryId === categoryId) reset();
      invalidate();
    },
    // 하위 카테고리·상품이 물려 있으면 서버가 409 로 이유를 내려 준다.
    onError: (e: Error) => setError(e.message),
  });

  function startEdit(category: FlatCategory) {
    setError(null);
    setForm({
      categoryId: category.categoryId,
      parentId: category.parentId != null ? String(category.parentId) : '',
      name: category.name,
      slug: category.slug,
      sortOrder: String(category.sortOrder),
      active: category.active,
    });
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    save.mutate({
      categoryId: form.categoryId,
      body: {
        parentId: form.parentId ? Number(form.parentId) : null,
        name: form.name.trim(),
        slug: form.slug.trim(),
        sortOrder: Number(form.sortOrder) || 0,
        active: form.active,
      },
    });
  }

  return (
    <>
      <h1 className="panel-title">카테고리 관리</h1>
      {feedback && <Alert tone="info">{feedback}</Alert>}

      <div className="card" style={{ marginBottom: 16 }}>
        <form className="card__body" onSubmit={handleSubmit}>
          <h2 style={{ fontSize: 16, marginTop: 0 }}>
            {isEdit ? `카테고리 수정 #${form.categoryId}` : '카테고리 등록'}
          </h2>
          {error && <Alert tone="error">{error}</Alert>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field">
              <label htmlFor="parentId">상위 카테고리</label>
              <select
                id="parentId"
                className="select"
                disabled={parentLocked}
                value={form.parentId}
                onChange={(e) => setForm((prev) => ({ ...prev, parentId: e.target.value }))}
              >
                <option value="">없음 (대분류)</option>
                {parentCandidates.map((category) => (
                  <option key={category.categoryId} value={category.categoryId}>
                    {category.name}
                  </option>
                ))}
              </select>
              {parentLocked && (
                <span className="muted" style={{ fontSize: 12 }}>
                  하위 카테고리가 있어 상위를 바꿀 수 없습니다. 2단계까지만 지원합니다.
                </span>
              )}
            </div>

            <div className="field">
              <label htmlFor="name">이름 *</label>
              <input
                id="name"
                className="input"
                required
                maxLength={100}
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="field">
              <label htmlFor="slug">slug *</label>
              <input
                id="slug"
                className="input"
                required
                maxLength={100}
                placeholder="skincare"
                value={form.slug}
                onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              />
            </div>

            <div className="field">
              <label htmlFor="sortOrder">정렬 순서</label>
              <input
                id="sortOrder"
                className="input"
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: e.target.value }))}
              />
            </div>
          </div>

          <div className="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 400 }}>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
              />
              <span>쇼핑몰에 노출 (해제하면 관리자에게만 보입니다)</span>
            </label>
          </div>

          <div className="form-actions">
            {isEdit && (
              <button type="button" className="btn btn--outline btn--sm" onClick={reset}>
                취소
              </button>
            )}
            <button type="submit" className="btn btn--primary btn--sm" disabled={save.isPending}>
              {save.isPending ? '저장 중…' : isEdit ? '수정 저장' : '등록'}
            </button>
          </div>
        </form>
      </div>

      {isLoading && <Loading />}
      {isError && <ErrorState error={loadError} onRetry={refetch} />}

      {data &&
        (flat.length === 0 ? (
          <Empty title="등록된 카테고리가 없습니다." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>이름</th>
                  <th>slug</th>
                  <th>정렬</th>
                  <th>노출</th>
                  <th>하위</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {flat.map((category) => (
                  <tr key={category.categoryId}>
                    <td>{category.categoryId}</td>
                    <td style={{ paddingLeft: category.depth > 1 ? 28 : undefined }}>
                      {category.depth > 1 && <span className="muted">└ </span>}
                      {category.name}
                    </td>
                    <td>{category.slug}</td>
                    <td>{category.sortOrder}</td>
                    <td>
                      {category.active ? (
                        <span className="badge badge--success">노출</span>
                      ) : (
                        <span className="badge badge--muted">숨김</span>
                      )}
                    </td>
                    <td>{category.childCount > 0 ? `${category.childCount}개` : '-'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          type="button"
                          className="btn btn--outline btn--sm"
                          onClick={() => startEdit(category)}
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`'${category.name}' 카테고리를 삭제할까요?`)) {
                              remove.mutate(category.categoryId);
                            }
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </>
  );
}
